import { useIntersection } from '@mantine/hooks'
import { ComponentProps } from '@stitches/react'
import { AppRoutes, LAYOUT_CENTER_COL_WIDTH } from 'config'
import { FeedType } from 'graphql/types'
import { useStore } from 'lib/store'
import { CSS } from 'lib/theme'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Post } from 'models/Post'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect } from 'react'
import { FeedEnd } from '../FeedEnd'
import { PostCard } from '../PostCard'

const feedItemCss: CSS = {
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: LAYOUT_CENTER_COL_WIDTH
}

export type FeedItemOnContentLoadedCb = (loadedElement: HTMLElement) => void

export interface FeedItemProps extends Omit<ComponentProps<typeof PostCard>, 'post'> {
  // Scroll container for feed items, which enables intersection observer tracking
  rootRef: React.MutableRefObject<HTMLElement | null>
  post?: Post
  // The index of the feed item in a virtualised feed, useful for scroll position restoration
  index: number
  // If different from index, provide ordinal that can be used to represent the number this feed
  // item is in the list of posts from the API. Feeds may have other elements in the feed and
  // therefore the index may not represent the post index/count in posts.
  ordinal?: number
  loading?: boolean
  error?: React.ReactNode | JSX.Element
  feedItems?: number
  category?: FeedType
  onContentLoaded?: FeedItemOnContentLoadedCb
}

export const FeedItemContext = React.createContext<{
  onContentLoaded: FeedItemOnContentLoadedCb
}>({
  // FeedItem dynamic content is loaded/ready/g2g/eshays.
  onContentLoaded: () => {}
})

const _FeedItem: React.FC<FeedItemProps> = ({
  post,
  index,
  ordinal = index,
  loading = false,
  error,
  css,
  feedItems,
  category,
  rootRef,
  onContentLoaded
}) => {
  const store = useStore()
  const router = useRouter()
  // console.log('post', post)
  const [ref, entry] = useIntersection({
    root: rootRef?.current,
    ...(store.ui.isMobile
      ? {
          // Intersecting element's top/bottom at the center of the viewport (captures both cases;
          // multiple posts on screen and post larger than viewport).
          threshold: 0,
          rootMargin: '-50% 0% -50% 0%'
        }
      : {
          // Desktop has play button, so we auto stop playing when 50% of the post has left the viewport.
          threshold: 0.5
        })
  })

  useEffect(() => {
    if (post) {
      // Keep track of the feed position of the post to be able to restore feed position,
      // when navigating back from a selected post.
      post.setFeedPosition(index, ordinal)
    }
  }, [post, index, ordinal])

  useEffect(() => {
    if (post && entry) {
      post.setIsIntersecting(entry.isIntersecting)
    }
  }, [post, entry])

  const handleContentLoaded = useCallback(
    (el: HTMLElement) => {
      onContentLoaded?.(el)
    },
    [onContentLoaded]
  )

  const handleEndFeedHead = () => {
    if (loading) return ''
    switch (true) {
      case feedItems === 0 && category === FeedType.FOLLOWING:
        return 'You do not have any followers.'
      case feedItems === 0 && category === FeedType.LIKED:
        return 'You have no liked posts.'
      default:
        return 'Visit our latest feed to explore more.'
    }
  }
  const handleEndFeedSubHead = () => {
    if (loading) return ''
    switch (true) {
      case feedItems === 0 && category === FeedType.FOLLOWING:
        return 'Visit our latest feed to explore more profiles.'
      case feedItems === 0 && category === FeedType.LIKED:
        return 'Visit our latest feed to explore more posts.'
      default:
        return 'Visit our latest feed to explore more.'
    }
  }

  return post && 'postId' in post ? (
    <FeedItemContext.Provider value={{ onContentLoaded: handleContentLoaded }}>
      <PostCard
        ref={ref}
        key={post.postId}
        post={post}
        css={{
          ...feedItemCss,
          ...css
        }}
        descMaxLines={3}
      />
    </FeedItemContext.Provider>
  ) : (
    <FeedEnd
      emptyState
      loading={loading}
      text={handleEndFeedHead()}
      subText={handleEndFeedSubHead()}
      callToAction="Check latest feed"
      onClick={() => router.push(AppRoutes.THREADS)}
      css={{
        marginBottom: '84px',
        maxWidth: `${LAYOUT_CENTER_COL_WIDTH}px`,
        ...feedItemCss,
        ...css
      }}
    />
  )
}

_FeedItem.displayName = 'FeedItem'

export const FeedItem = observer(_FeedItem)
